import React from 'react';

const EstimateCard = React.forwardRef(({ estimate }, ref) => {
  const fmt = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
  const hasImages = estimate.images && estimate.images.length > 0;
  const makingLabel = estimate.making_charges_type === 'fixed'
    ? `Fixed: ${fmt(estimate.fixed_making_charge || 0)}`
    : `${fmt(estimate.making_charges_per_gram || 0)}/g × ${estimate.net_weight?.toFixed(2) || 0}g`;

  return (
    <div ref={ref} style={{
      width: '600px',
      background: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      color: '#1f2937',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #064e3b 0%, #047857 100%)',
        color: '#fff',
        padding: '20px 24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '800', letterSpacing: '3px' }}>JEWELFLOW</h1>
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px', letterSpacing: '1px' }}>JEWELLERY ESTIMATES</div>
          </div>
          <span style={{
            background: estimate.status === 'confirmed' ? '#34d399' : '#fbbf24',
            color: estimate.status === 'confirmed' ? '#064e3b' : '#78350f',
            padding: '3px 14px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {estimate.status === 'confirmed' ? 'Confirmed' : 'Draft'}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '14px' }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: '700' }}>{estimate.estimate_number}</div>
            <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '2px' }}>
              {new Date(estimate.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {estimate.tag_number && (
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Tag: <b>{estimate.tag_number}</b></div>
            )}
            <div style={{ fontSize: '12px', opacity: 0.8 }}>
              {estimate.main_group && estimate.product
                ? `${estimate.main_group} - ${estimate.product}`
                : estimate.main_group || estimate.product || ''}
            </div>
            {estimate.style_name && (
              <div style={{ fontSize: '11px', opacity: 0.75, marginTop: '1px' }}>{estimate.style_name}</div>
            )}
            {estimate.hsn_code && (
              <div style={{ fontSize: '10px', opacity: 0.7 }}>HSN: {estimate.hsn_code}</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Product Image ── */}
      {hasImages && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 24px 8px', background: '#f9fafb' }}>
          <img
            src={estimate.images[0]}
            alt="Product"
            style={{
              maxWidth: '200px',
              maxHeight: '180px',
              objectFit: 'cover',
              borderRadius: '10px',
              border: '2px solid #e5e7eb',
            }}
            crossOrigin="anonymous"
          />
        </div>
      )}

      {/* ── Weight Summary Row ── */}
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{
          display: 'flex',
          gap: '8px',
        }}>
          {[
            { label: 'Gross Wt', value: `${estimate.gross_weight}g` },
            { label: 'Stone Wt', value: `${estimate.stones_weight_carats?.toFixed(2) || 0}ct` },
            { label: 'Net Wt', value: `${estimate.net_weight?.toFixed(2) || 0}g` },
            { label: 'Purity', value: estimate.gold_purity || '22K' },
          ].map((item, i) => (
            <div key={i} style={{
              flex: 1,
              textAlign: 'center',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '8px',
              padding: '10px 4px',
            }}>
              <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#064e3b', marginTop: '2px' }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Wastage & Gold Rate Row ── */}
      <div style={{ padding: '10px 24px 0' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#fefce8',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            padding: '10px 14px',
          }}>
            <span style={{ fontSize: '12px', color: '#92400e', fontWeight: '600' }}>Wastage</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#92400e' }}>{estimate.wastage_percentage}%</span>
          </div>
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            borderRadius: '8px',
            padding: '10px 14px',
          }}>
            <span style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>Gold Rate</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#065f46' }}>{fmt(estimate.gold_rate)}/g</span>
          </div>
        </div>
      </div>

      {/* ── Gold Amount ── */}
      <div style={{ padding: '10px 24px 0' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          padding: '12px 14px',
        }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#78350f' }}>Gold Amount</span>
          <span style={{ fontSize: '18px', fontWeight: '800', color: '#92400e' }}>{fmt(estimate.gold_amount)}</span>
        </div>
      </div>

      {/* ── Stones Breakdown ── */}
      {estimate.stones && estimate.stones.length > 0 && (
        <div style={{ padding: '14px 24px 0' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b21a8', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Stones ({estimate.stones.length})
          </div>
          <div style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            {estimate.stones.map((stone, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 14px',
                borderBottom: idx < estimate.stones.length - 1 ? '1px solid #f3e8ff' : 'none',
                fontSize: '13px',
              }}>
                <span style={{ color: '#6b7280', flex: 1 }}>
                  {stone.name || 'Stone'}: {stone.weight}ct @ {fmt(stone.rate)}/ct
                </span>
                <span style={{ fontWeight: '700', color: '#7c3aed', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                  {fmt(stone.amount)}
                </span>
              </div>
            ))}
          </div>
          {/* Stones Total */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 14px',
            background: '#ede9fe',
            borderRadius: '0 0 8px 8px',
            marginTop: '-1px',
            border: '1px solid #e9d5ff',
            borderTop: '2px solid #c4b5fd',
          }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#6b21a8' }}>Stones Total</span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#6b21a8' }}>{fmt(estimate.stones_amount)}</span>
          </div>
        </div>
      )}

      {/* ── Making Charges ── */}
      <div style={{ padding: '10px 24px 0' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: '8px',
          padding: '12px 14px',
        }}>
          <div>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#9a3412' }}>Making Charges</span>
            <div style={{ fontSize: '11px', color: '#c2410c', marginTop: '2px' }}>{makingLabel}</div>
          </div>
          <span style={{ fontSize: '18px', fontWeight: '800', color: '#9a3412' }}>{fmt(estimate.making_amount)}</span>
        </div>
      </div>

      {/* ── Other Charges ── */}
      {estimate.other_charges > 0 && (
        <div style={{ padding: '10px 24px 0' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 14px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '13px',
          }}>
            <span style={{ color: '#6b7280', fontWeight: '600' }}>Other Charges</span>
            <span style={{ fontWeight: '700', color: '#374151' }}>{fmt(estimate.other_charges)}</span>
          </div>
        </div>
      )}

      {/* ── Final Summary ── */}
      <div style={{ padding: '16px 24px' }}>
        <div style={{
          border: '2px solid #047857',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          {/* Line items */}
          {[
            { label: 'Gold Amount', value: estimate.gold_amount, color: '#92400e' },
            { label: 'Stone Amount', value: estimate.stones_amount, color: '#7c3aed' },
            { label: 'Making Amount', value: estimate.making_amount, color: '#9a3412' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 16px',
              borderBottom: '1px solid #d1fae5',
              fontSize: '14px',
            }}>
              <span style={{ color: '#374151', fontWeight: '500' }}>{item.label}</span>
              <span style={{ fontWeight: '700', color: item.color }}>{fmt(item.value)}</span>
            </div>
          ))}
          {estimate.other_charges > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 16px',
              borderBottom: '1px solid #d1fae5',
              fontSize: '14px',
            }}>
              <span style={{ color: '#374151', fontWeight: '500' }}>Other Charges</span>
              <span style={{ fontWeight: '700', color: '#374151' }}>{fmt(estimate.other_charges)}</span>
            </div>
          )}
          {/* Grand Total */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #064e3b 0%, #047857 100%)',
            color: '#fff',
          }}>
            <span style={{ fontSize: '15px', fontWeight: '700', letterSpacing: '0.5px' }}>GRAND TOTAL (All Inclusive)</span>
            <span style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '0.5px' }}>{fmt(estimate.total_amount)}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

EstimateCard.displayName = 'EstimateCard';

export default EstimateCard;
